import { EuiButton, EuiButtonIcon, EuiFlexGroup, EuiFlexItem, EuiSteps } from "@elastic/eui";
import React, { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { extractResourceAttributes } from "../../../../utils/jsonApiUtils";
import AbstractDropdown from "../../../shared/entity/components/AbstractFormField/fields/AbstractDropdown";
import { createEntity } from "../../../shared/api";
import dropdownFieldsOptions from "../../configs/dropdownFields";
import * as S from "../../style";
import _ from "lodash";
import ConfirmationModal from "../../../shared/modals/ConfirmationModal";
import { useIntl } from "react-intl";

const generateOptionsByEntity = (entityData) =>
    entityData && entityData.id
        ? [
              {
                  value: entityData.id,
                  label: entityData.name,
              },
          ]
        : [];

const dropOffsFormModel = {
    city: {
        id: "",
        name: "",
    },
    location: { id: "", name: "" },
    governorate: {
        id: "",
        name: "",
    },
};

function DropoffsForm({
    branchLocation,
    tripType,
    onChange,
    onDelete,
    selectedDropOffs,
    canAppend = true,
    organizationId,
}) {
    const [dropOffsFields, setDropOffsFields] = useState(
        selectedDropOffs && selectedDropOffs.length ? _.cloneDeep(selectedDropOffs) : []
    );
    const intl = useIntl();
    const [deletingDropoffId, setDeletingDropoffId] = useState(null);
    const deletingDropoff =
        selectedDropOffs && selectedDropOffs.find((point) => point.id === deletingDropoffId);

    useEffect(() => {
        if (selectedDropOffs) {
            setDropOffsFields(_.cloneDeep(selectedDropOffs));
        }
    }, [selectedDropOffs]);

    const addDropOffField = () => {
        const newFormValues = [..._.cloneDeep(dropOffsFields), _.cloneDeep(dropOffsFormModel)];

        setDropOffsFields(newFormValues);
        onChange(_.cloneDeep(newFormValues));
    };

    const pickupCityName =
        branchLocation &&
        (branchLocation.cityName || branchLocation.locationAttributes.data.attributes.cityName);
    const pickupAddress =
        branchLocation &&
        (branchLocation.name ||
            branchLocation.address ||
            branchLocation.locationAttributes?.data?.attributes?.nameAr ||
            branchLocation.locationAttributes?.data?.attributes?.name ||
            branchLocation.locationAttributes?.data?.attributes?.address);

    const addFormStep = {
        title: "",
        children: (
            <>
                <EuiButton
                    color="secondary"
                    onClick={addDropOffField}
                    className="dropoff-form__add-button"
                    disabled={!organizationId && !branchLocation}
                >
                    {intl.formatMessage({ id: "trip-dropoffs.add-dropoff-action-label" })}
                </EuiButton>
            </>
        ),
        status: "disabled",
        className: "add-step",
    };

    const editDropoffData = (dataKey, fieldIndex, selectedOption, selectedEntity) => {
        const { label, value } = selectedOption || { label: "", value: null };
        const formClone = _.cloneDeep(dropOffsFields);

        formClone[fieldIndex][dataKey] = {
            id: value,
            name: label,
        };

        if (dataKey === "category") {
            formClone[fieldIndex].location = {
                id: null,
                name: "",
            };
            formClone[fieldIndex].city = {
                id: null,
                name: "",
            };
            formClone[fieldIndex].governorate = {
                id: null,
                name: "",
            };
        }

        if (dataKey === "governorate") {
            formClone[fieldIndex].city = {
                id: null,
                name: "",
            };
            formClone[fieldIndex].location = {
                id: null,
                name: "",
            };
        }

        if (dataKey === "city") {
            formClone[fieldIndex].location = {
                id: null,
                name: "",
            };
        }

        if (dataKey === "location" && selectedEntity) {
            formClone[fieldIndex].city = {
                id: selectedEntity.attributes?.cityId || null,
                name: selectedEntity.attributes?.cityName || "",
            };
            formClone[fieldIndex].governorate = {
                id: selectedEntity.attributes?.governorateId || null,
                name: selectedEntity.attributes?.governorateName || "",
            };
            formClone[fieldIndex].category = {
                id: selectedEntity.attributes?.locationCategoryId || null,
                name: selectedEntity.attributes?.locationCategoryName || "",
            };
        }

        setDropOffsFields(formClone);
        onChange(_.cloneDeep(formClone));
    };

    const deleteDropOff = (index) => {
        const newForm = _.cloneDeep(dropOffsFields);

        newForm.splice(index, 1);
        setDropOffsFields(newForm);
        onChange(_.cloneDeep(newForm));
    };

    const createLocation = async (selectedOption, fieldIndex) => {
        const { label } = selectedOption;
        const cityId = +dropOffsFields[fieldIndex].city.id;

        try {
            const response = await createEntity("locations", {
                name: label,
                cityId,
                organizationId,
            });

            const addedEntity = extractResourceAttributes(response.entities.locations);
            selectedOption = generateOptionsByEntity(addedEntity)[0];
            editDropoffData("location", fieldIndex, selectedOption);
        } catch (e) {
            console.log(e);
        }
    };

    const handlePointDelete = (index) => {
        const deletedPointId = selectedDropOffs[index].id;

        if (deletedPointId) {
            setDeletingDropoffId(deletedPointId);
        } else {
            deleteDropOff(index);
        }
    };

    const generatePickupStep = () => {
        return {
            title: <FormattedMessage id="trip-form.pickup-section-title" />,
            children: (
                <EuiFlexGroup
                    className="step-container"
                    gutterSize="s"
                >
                    <EuiFlexItem>
                        <AbstractDropdown
                            entityOptions={dropdownFieldsOptions.city}
                            selectedOptions={branchLocation && [{ label: pickupCityName }]}
                            isDisabled={true}
                        />
                    </EuiFlexItem>

                    <EuiFlexItem className="dropdown-container--location">
                        <AbstractDropdown
                            entityOptions={dropdownFieldsOptions.location}
                            selectedOptions={
                                branchLocation && [
                                    {
                                        label: pickupAddress,
                                    },
                                ]
                            }
                            isDisabled={true}
                        />
                    </EuiFlexItem>
                </EuiFlexGroup>
            ),
        };
    };

    const generateDropOffSteps = (dropoff, i, dropOffs) => {
        const pointTitle = intl.formatMessage(
            { id: "trip-dropoffs.dropoff-number" },
            { number: i + 1 }
        );
        const showDeleteBtn = dropoff.id
            ? dropoff.canModify &&
              dropOffs.length !== 1 &&
              ((branchLocation && i > 0) || !branchLocation)
            : true;

        const isDropoffDisabled =
            dropoff.id && (!dropoff.category?.id || !dropoff.location?.id)
                ? !dropoff.canModify
                : false;
        return {
            title: pointTitle,
            children: (
                <EuiFlexGroup
                    className="step-container"
                    gutterSize="s"
                >
                    <EuiFlexItem>
                        <AbstractDropdown
                            entityOptions={{
                                ...dropdownFieldsOptions.category,
                                filterResource: ` filter[governorate_id]=${dropoff?.governorate?.id}`,
                            }}
                            onValueChange={(selectedOptions, selectedEntity) =>
                                editDropoffData("category", i, selectedOptions[0], selectedEntity)
                            }
                            selectedOptions={generateOptionsByEntity(dropoff.category)}
                        />
                    </EuiFlexItem>

                    <EuiFlexItem>
                        <AbstractDropdown
                            entityOptions={{
                                ...dropdownFieldsOptions.governorate,
                                filterResource: `${
                                    tripType?.locationRequired
                                        ? `filter[organization_id]=${organizationId}&`
                                        : ""
                                }filter[location_category_id]=${dropoff?.category?.id}`,
                            }}
                            onValueChange={(selectedOptions, selectedEntity) =>
                                editDropoffData(
                                    "governorate",
                                    i,
                                    selectedOptions[0],
                                    selectedEntity
                                )
                            }
                            entityObject={{ entityName: "governorates" }}
                            selectedOptions={generateOptionsByEntity(dropoff.governorate)}
                            isDisabled={isDropoffDisabled || !dropoff.category?.id}
                        />
                    </EuiFlexItem>

                    <EuiFlexItem>
                        <AbstractDropdown
                            entityOptions={{
                                ...dropdownFieldsOptions.city,
                                validation: {
                                    required: tripType && !!tripType.cityRequired,
                                },
                                filterResource:
                                    dropoff.governorate &&
                                    `filter[governorateId]=${dropoff?.governorate?.id}${
                                        tripType?.locationRequired
                                            ? `&filter[organization_id]=${organizationId}`
                                            : ""
                                    }`,
                            }}
                            onValueChange={(selectedOptions, selectedEntity) =>
                                editDropoffData("city", i, selectedOptions[0], selectedEntity)
                            }
                            entityObject={{ entityName: "cities" }}
                            selectedOptions={generateOptionsByEntity(dropoff.city)}
                            isDisabled={isDropoffDisabled || !dropoff.governorate?.id}
                        />
                    </EuiFlexItem>

                    <EuiFlexItem className="dropdown-container--location">
                        <AbstractDropdown
                            entityOptions={{
                                ...dropdownFieldsOptions.location,
                                validation: {
                                    required: tripType && !!tripType.locationRequired,
                                },
                                filterResource: `${
                                    dropoff.category?.id
                                        ? `&filter[locationCategoryId]=${dropoff.category.id}`
                                        : ""
                                }${
                                    dropoff.governorate?.id
                                        ? `&filter[governorateId]=${dropoff.governorate.id}`
                                        : ""
                                }${dropoff.city?.id ? `&filter[cityId]=${dropoff.city.id}` : ""}
                                ${`&filter[organization_id]=${organizationId}`}
                                ${`&filter[name_ar]`}
                                `,
                            }}
                            onValueChange={(selectedOptions, entityOptionsName, selectedEntity) =>
                                editDropoffData("location", i, selectedOptions[0], selectedEntity)
                            }
                            entityObject={{ entityName: "locations" }}
                            onCreateEntity={(selectedOptions) => createLocation(selectedOptions, i)}
                            selectedOptions={generateOptionsByEntity(dropoff.location)}
                        />
                    </EuiFlexItem>
                    {showDeleteBtn && (
                        <EuiButtonIcon
                            color="danger"
                            onClick={() => handlePointDelete(i)}
                            iconType="trash"
                            aria-label="Delete"
                            className="dropoff-form__delete-btn"
                        />
                    )}
                </EuiFlexGroup>
            ),
            status: dropoff.city.id ? "complete" : "danger",
        };
    };

    return (
        <S.TripStepsFormWrapper>
            <EuiSteps
                titleSize="xs"
                steps={
                    canAppend
                        ? [
                              generatePickupStep(),
                              ...dropOffsFields.map(generateDropOffSteps),
                              addFormStep,
                          ]
                        : [generatePickupStep(), ...dropOffsFields.map(generateDropOffSteps)]
                }
            />

            {deletingDropoffId !== null && (
                <ConfirmationModal
                    title={intl.formatMessage(
                        { id: "trip-dropoffs.delete-dropoff-number" },
                        { number: deletingDropoffId }
                    )}
                    content={
                        <>
                            {intl.formatMessage(
                                { id: "trip-dropoffs.delete-dropoff-confirmation" },
                                { address: deletingDropoff?.location?.name || "" }
                            )}
                        </>
                    }
                    onConfirm={() => {
                        onDelete(+deletingDropoffId);
                        setDeletingDropoffId(null);
                    }}
                    confirmButtonText={intl.formatMessage({ id: "misc.ok" })}
                    onCancel={() => setDeletingDropoffId(null)}
                />
            )}
        </S.TripStepsFormWrapper>
    );
}

export default DropoffsForm;
